import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../components/PageTitle';
import PageLayout from '../components/PageLayout';
import { Place as PlaceIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase';
import { collection, onSnapshot, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { Location } from '../types/Location';
import { Button, Grid, Card, CardMedia, CardContent, Typography, CardActions, IconButton, Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { LocationDialog } from '../components/LocationDialog';
import LocationPopUpDialog from '../components/LocationPopUpDialog';
import { adminStyles } from '../theme/theme';

export const LocationsPage: React.FC = () => {
    // const navigate = useNavigate(); // Navigation no longer needed for details
    const { isAdmin } = useAuth();
    const [locations, setLocations] = useState<Location[]>([]);
    const [loading, setLoading] = useState(true);

    // State for Edit/Create Dialog
    const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // State for Delete Confirmation
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);

    // State for View Details Popup
    const [viewLocationId, setViewLocationId] = useState<string | null>(null);

    useEffect(() => {
        const q = query(collection(db, 'locations'), orderBy('name'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const locs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as Location));
            setLocations(locs);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleAdd = () => {
        setSelectedLocation(null);
        setIsDialogOpen(true);
    };

    const handleEdit = (location: Location) => {
        setSelectedLocation(location);
        setIsDialogOpen(true);
    };

    const handleDeleteClick = (location: Location) => {
        setLocationToDelete(location);
        setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (locationToDelete && locationToDelete.id) {
            try {
                // Collecter toutes les URLs de photos à supprimer
                const photosToDelete: string[] = [];

                if (locationToDelete.coverPhotoUrl) {
                    photosToDelete.push(locationToDelete.coverPhotoUrl);
                }

                if (locationToDelete.gallery) {
                    locationToDelete.gallery.forEach(photo => {
                        if (photo.url) photosToDelete.push(photo.url);
                    });
                }

                // Legacy support
                if (locationToDelete.photoUrls) {
                    locationToDelete.photoUrls.forEach(url => photosToDelete.push(url));
                }

                // Supprimer les fichiers du Storage
                await Promise.all(photosToDelete.map(async (url) => {
                    try {
                        const photoRef = ref(storage, url);
                        await deleteObject(photoRef);
                    } catch (err) {
                        console.warn(`Impossible de supprimer la photo ${url} (peut-être déjà supprimée)`, err);
                    }
                }));

                // Supprimer le document Firestore
                await deleteDoc(doc(db, 'locations', locationToDelete.id));
                setDeleteConfirmOpen(false);
                setLocationToDelete(null);
            } catch (error) {
                console.error("Erreur lors de la suppression:", error);
                alert("Erreur lors de la suppression.");
            }
        }
    };

    return (
        <PageLayout>
            <PageTitle
                title="Nos Salles"
                icon={<PlaceIcon fontSize="large" />}
                action={isAdmin ? (
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                    >
                        Ajouter un lieu
                    </Button>
                ) : undefined}
            />

            <div className="p-4">
                {locations.length === 0 && !loading ? (
                    <Typography variant="body1" color="text.secondary" align="center">
                        Aucun lieu enregistré pour le moment.
                    </Typography>
                ) : (
                    <Grid container spacing={4}>
                        {locations.map((location) => (
                            <Grid item xs={12} sm={6} md={4} key={location.id}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'scale(1.02)'
                                        }
                                    }}
                                    onClick={() => {
                                        if (location.id) setViewLocationId(location.id);
                                    }}
                                >
                                    <Box sx={{ position: 'relative' }}>
                                        {location.coverPhotoUrl ? (
                                            <CardMedia
                                                component="img"
                                                height="200"
                                                image={location.coverPhotoUrl}
                                                alt={location.name}
                                            />
                                        ) : location.photoUrls && location.photoUrls.length > 0 ? (
                                            <CardMedia
                                                component="img"
                                                height="200"
                                                image={location.photoUrls[0]}
                                                alt={location.name}
                                            />
                                        ) : (
                                            <Box sx={{ height: 200, bgcolor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <PlaceIcon sx={{ fontSize: 60, color: 'grey.500' }} />
                                            </Box>
                                        )}

                                        {isAdmin && (
                                            <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Stop propagation to prevent opening details
                                                        handleEdit(location);
                                                    }}
                                                    sx={{
                                                        ...adminStyles.glassButton,
                                                        '&:hover': {
                                                            ...adminStyles.glassButton['&:hover'],
                                                            bgcolor: 'primary.main'
                                                        }
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Stop propagation to prevent opening details
                                                        handleDeleteClick(location);
                                                    }}
                                                    sx={{
                                                        ...adminStyles.glassButton,
                                                        '&:hover': {
                                                            ...adminStyles.glassButton['&:hover'],
                                                            bgcolor: 'error.main'
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Box>

                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography gutterBottom variant="h5" component="div">
                                            {location.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph noWrap>
                                            {location.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </div>

            {/* Edit/Create Dialog */}
            <LocationDialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                locationToEdit={selectedLocation}
            />

            {/* View Details Popup */}
            <LocationPopUpDialog
                open={!!viewLocationId}
                locationId={viewLocationId}
                onClose={() => setViewLocationId(null)}
            />

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir supprimer le lieu "{locationToDelete?.name}" ?
                        Cette action est irréversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">
                        Annuler
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" autoFocus>
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>


        </PageLayout>
    );
};
